/**
 * GoogleSignInButton.jsx
 *
 * Reusable "Continue with Google" button. It sends the browser to our own
 * backend, which owns the Google OAuth client. The caller specifies the role
 * intent (`user` for readers, `pending_writer` for new writer applicants); the
 * backend honours it only for brand-new accounts.
 */
import React from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const GoogleIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M12 5.04c1.93 0 3.66.66 5.02 1.95l3.74-3.74C18.49 1.18 15.51 0 12 0 7.31 0 3.26 2.69 1.28 6.6l4.34 3.37C6.66 7.04 9.11 5.04 12 5.04z"
    />
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.54h6.45c-.28 1.49-1.13 2.75-2.4 3.6l3.7 2.87c2.16-2 3.74-4.94 3.74-8.74z"
    />
    <path
      fill="#FBBC05"
      d="M5.62 14.27c-.21-.66-.34-1.37-.34-2.27 0-.9.13-1.61.34-2.27L1.28 6.36C.46 7.96 0 9.96 0 12c0 2.04.46 4.04 1.28 5.64l4.34-3.37z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.51 0 6.49-1.16 8.65-3.13l-3.7-2.87c-1.04.7-2.4 1.13-4.95 1.13-2.89 0-5.34-2-6.38-4.93l-4.34 3.37C3.26 21.31 7.31 24 12 24z"
    />
    <path fill="none" d="M0 0h24v24H0z" />
  </svg>
);

/**
 * @param {object} props
 * @param {'user' | 'pending_writer'} props.role - The role to assign if this is a brand-new sign-up.
 * @param {string} props.label - Button copy (e.g. "Continue with Google").
 * @param {string} [props.testId] - data-testid for E2E tests.
 */
const GoogleSignInButton = ({ role, label = 'Continue with Google', testId = 'google-signin-btn' }) => {
  const handleClick = () => {
    // Our own Google OAuth client, started server-side: the backend redirects to
    // Google, verifies the identity token on the callback, creates the session
    // cookie and sends the browser back to `next`.
    const params = new URLSearchParams({ next: '/dashboard' });
    if (role) params.set('role', role);
    window.location.href = `${BACKEND_URL}/api/auth/google/start?${params.toString()}`;
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={testId}
      className="w-full py-3.5 px-4 rounded-md bg-white text-slate-800 hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center justify-center gap-3 font-medium shadow-sm border border-slate-200/40"
    >
      <GoogleIcon />
      <span>{label}</span>
    </button>
  );
};

export default GoogleSignInButton;
